import React, {
  useMemo, useEffect, useRef, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useModel } from '@src/generic/model-store';
import { getCourseOutline } from '@src/courseware/data/selectors';
import { getCourseOutlineStructure } from '@src/courseware/data/thunks';

import IgeaHeader from './IgeaHeader';
import IgeaSidebar from './IgeaSidebar';
import IgeaMainContent from './IgeaMainContent';

/**
 * IGEA: Pixel-perfect player layout replacing the default edX Course UI.
 * - Reads course outline + current unit from Redux.
 * - Renders minimal header, custom sidebar, clean iframe main content.
 * - Matches the Dribbble design tokens (white cards, 320px sidebar, Inter font).
 */

// Detect lesson type from edX block data.
// For IGEA, all sequentials default to video (since courses are video-based).
const inferLessonType = (block) => {
  const cat = (block?.category || block?.type || '').toLowerCase();
  if (cat.includes('video')) { return 'video'; }
  if (cat.includes('problem') || cat.includes('quiz')) { return 'quiz'; }
  if (cat.includes('html') || cat.includes('text')) { return 'reading'; }
  if (cat.includes('discussion')) { return 'discussion'; }
  if (cat.includes('exam') || cat.includes('assignment')) { return 'exam'; }
  // IGEA: treat all sequentials and verticals as "video" (default lesson type)
  if (cat === 'vertical' || cat === 'sequential' || cat === 'lock') { return 'video'; }
  return 'video';
};

// Format a duration in seconds as "MM:SS" (or "H:MM:SS" if ≥1h).
// Returns null if input is not a positive finite number.
const formatDuration = (seconds) => {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) { return null; }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  const pad = (n) => String(n).padStart(2, '0');
  if (h > 0) { return `${h}:${pad(m)}:${pad(sec)}`; }
  return `${pad(m)}:${pad(sec)}`;
};

const IgeaPlayerLayout = ({ courseId, sequenceId, unitId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const course = useModel('coursewareMeta', courseId);
  const outline = useSelector(getCourseOutline);

  // L'outline porta lo stato di completamento, ed è quello che dice alla
  // sidebar dove sei arrivato e cosa è sbloccato. Prima veniva chiesto una
  // volta sola per corso: avanzando di lezione la sidebar restava alla
  // fotografia di quando eri entrato.
  //
  // Si richiede a ogni cambio di lezione — è l'unico momento in cui il
  // completamento può essere cambiato — tenendo il riferimento per (corso,
  // lezione) così da non ripartire in ciclo se un altro componente lascia
  // `outlineStatus` in "loading".
  const fetchedFor = useRef(null);
  useEffect(() => {
    if (!courseId) { return; }
    const chiave = `${courseId}|${sequenceId || ''}`;
    if (fetchedFor.current === chiave) { return; }
    fetchedFor.current = chiave;
    dispatch(getCourseOutlineStructure(courseId));
  }, [courseId, sequenceId, dispatch]);

  // Build an ordered list of sequence IDs across all sections so we can
  // jump to the next one when the XBlock fires `igea:advance-next-unit`.
  // Stessa lista, passata anche a IgeaMainContent per i pulsanti "Lezione
  // precedente/successiva": prima li ricalcolava da sole leggendo un altro
  // reducer (quello classico di `fetchCourse`, popolato una sola volta e mai
  // riprovato), e se quella fetch falliva la sidebar si riprendeva al giro
  // dopo mentre i pulsanti restavano ciechi per tutta la sessione. Un'unica
  // fonte, quella che sappiamo funzionare — è la stessa da cui esce la
  // sidebar — chiude la divergenza.
  const orderedSeqIds = useMemo(() => {
    if (!outline?.sections) { return []; }
    return Object.values(outline.sections)
      .flatMap((s) => s.sequenceIds || []);
  }, [outline]);

  // Listen for the auto-advance postMessage from the IGEA XBlock iframe.
  // The XBlock can't reach the React Router itself, so it asks us to
  // navigate when its 5-second countdown ends.
  const handleAdvanceMessage = useCallback((event) => {
    if (event?.data?.type !== 'igea:advance-next-unit') { return; }
    if (!sequenceId || orderedSeqIds.length === 0) { return; }
    const i = orderedSeqIds.indexOf(sequenceId);
    if (i === -1 || i >= orderedSeqIds.length - 1) {
      // End of the course — no next unit. Nothing to do.
      return;
    }
    const nextId = orderedSeqIds[i + 1];
    navigate(`/course/${courseId}/${nextId}`);
  }, [courseId, sequenceId, orderedSeqIds, navigate]);

  useEffect(() => {
    window.addEventListener('message', handleAdvanceMessage);
    return () => window.removeEventListener('message', handleAdvanceMessage);
  }, [handleAdvanceMessage]);

  // #238 (cap. 3 Agenas): dopo un tentativo del test ECM non superato il corso
  // riparte dalla prima lezione. A chiederlo e' il sito (CoursePlayerModal),
  // l'unico a sapere com'e' andato il test; qui si conosce l'indice del corso
  // e si naviga alla prima unita' della prima lezione. Come `igea:advance-
  // next-unit`, e' un campanello senza dati.
  const handlePrimaLezione = useCallback((event) => {
    if (event?.data?.type !== 'igea:vai-alla-prima-lezione') { return; }
    if (!courseId || orderedSeqIds.length === 0) { return; }
    const primaLezione = orderedSeqIds[0];
    const primaUnita = outline?.sequences?.[primaLezione]?.unitIds?.[0];
    navigate(primaUnita
      ? `/course/${courseId}/${primaLezione}/${primaUnita}`
      : `/course/${courseId}/${primaLezione}`);
  }, [courseId, orderedSeqIds, outline, navigate]);

  useEffect(() => {
    window.addEventListener('message', handlePrimaLezione);
    return () => window.removeEventListener('message', handlePrimaLezione);
  }, [handlePrimaLezione]);

  // L'unità avvisa quando il completamento è stato registrato (lo pubblica lei,
  // vedi unit_gate.py in igea-edx). La spunta nella sidebar viene dall'outline,
  // quindi va richiesto di nuovo: senza questo comparirebbe solo al cambio di
  // lezione, che è il motivo per cui la sidebar sembrava non aggiornarsi mai.
  const handleCompletedMessage = useCallback((event) => {
    if (event?.data?.type !== 'igea:unit-completed' || !courseId) { return; }
    dispatch(getCourseOutlineStructure(courseId));
  }, [courseId, dispatch]);

  useEffect(() => {
    window.addEventListener('message', handleCompletedMessage);
    return () => window.removeEventListener('message', handleCompletedMessage);
  }, [handleCompletedMessage]);

  // Se in questo corso ci sia propedeuticita' lo dice l'unita': `vincolato` sul
  // messaggio `igea:unit-gate` e' vero solo per le iscrizioni in modalita' ECM
  // (vedi unit_gate.py in igea-edx). E' una proprieta' dell'iscrizione, non
  // della singola lezione, quindi una volta appresa resta: le unita' successive
  // potrebbero non riuscire ad annunciarsi, e non e' un buon motivo per aprire
  // tutta la sidebar.
  //
  // Serve perche' prima il lucchetto era incondizionato: anche un corso venduto
  // senza vincoli mostrava le lezioni bloccate e con href nullo, cioe' non
  // raggiungibili, pur essendo tutte legittimamente accessibili.
  //
  // LIMITE, ed e' piu' largo di come e' stato scritto la prima volta ("solo la
  // prima unita'"). Non e' la prima unita': e' finche' *nessuna* unita' parla.
  // Se lo script non viene iniettato, se il suo JS muore, se l'iframe non
  // carica, qui non arriva mai niente e `propedeuticita` resta falsa per tutta
  // la vita del componente — su ogni lezione, non solo sulla prima. Con quella
  // falsa la sidebar non mostra lucchetti e IgeaMainContent, scaduta l'attesa
  // senza risposta, apre il pulsante "Lezione successiva".
  //
  // Quanto costa: e' una bugia nell'interfaccia, non contenuto che esce. Le
  // unita' chiuse le nega comunque il server (IgeaPropedeuticitaMiddleware in
  // igea-edx, su /xblock/ e sugli handler), quindi il pulsante promette una
  // lezione che poi viene rifiutata. Il caso "aperto dai due lati" — backend
  // irraggiungibile — non e' questo difetto: li' `vincolo_attivo` risponde
  // "non vincolato" a entrambi, ed e' il fail-open voluto di propedeuticita.py.
  //
  // Perche' non si legge il flag e basta: il flag di corso
  // (`prerequisites_enabled` su /api/webhooks/edx/certificate-mode) e' un
  // webhook autenticato con segreto condiviso, che consuma edX da server a
  // server. Questo e' codice di browser: il segreto non ce l'ha e non deve
  // averlo. E oggi edX non lo ripubblica su nessun canale che il player legga
  // gia' — niente rotta in plugins/igea, niente nei metadati del courseware ne'
  // in /api/learning_sequences/v1/course_outline — e il frontend Nuxt che
  // incornicia il player ascolta soltanto, non scrive mai nell'iframe. Chiuderlo
  // vuol dire aggiungere il flag all'outline (o ai metadati del corso) che il
  // MFE gia' scarica: e' una modifica a igea-edx, non a questo repo.
  // Stesso flag usato anche per mostrare l'indicazione "Modalità ECM" in
  // sidebar (IgeaProgressCard, via IgeaSidebar): niente fonte nuova, e lo
  // stesso "parte falso, diventa vero solo con la conferma" fa gia' il
  // lavoro giusto anche li' — nessuna etichetta finche' non e' confermato,
  // ne' per il percorso libero ne' mentre non e' ancora noto.
  const [propedeuticita, setPropedeuticita] = useState(false);
  useEffect(() => { setPropedeuticita(false); }, [courseId]);
  useEffect(() => {
    const onGate = (event) => {
      // `percorsoVincolato` (collaudo P-05, edx #108) e' la proprieta'
      // dell'iscrizione e arriva anche da un'unita' gia' completata, che per
      // il proprio cancello dice `vincolato: false`.
      if (event?.data?.type === 'igea:unit-gate'
        && (event.data.vincolato || event.data.percorsoVincolato)) {
        setPropedeuticita(true);
      }
    };
    window.addEventListener('message', onGate);
    return () => window.removeEventListener('message', onGate);
  }, []);

  // Transform outline Redux structure → sidebar modules[].
  // Walks sections in order and applies progressive-unlock logic:
  //   - A lesson is "locked" if any earlier lesson in the same module
  //     OR any earlier module is not fully completed.
  //   - A module is "locked" if any earlier module is not fully completed.
  // Status per lesson: 'completed' | 'in_progress' | 'not_started'.
  const modules = useMemo(() => {
    if (!outline?.sections) { return []; }
    const { sections = {}, sequences = {} } = outline;

    // Track frontier flags as we walk.
    let prevModuleCompleted = true; // first module always unlocked
    return Object.values(sections).map((section) => {
      const sequenceIds = section.sequenceIds || [];
      const lessonSeqs = sequenceIds.map((sid) => sequences[sid]).filter(Boolean);
      // Nemmeno il modulo che contiene la lezione corrente può essere bloccato.
      const moduleLocked = propedeuticita
        && !prevModuleCompleted && !sequenceIds.includes(sequenceId);

      let prevLessonCompleted = true; // first lesson of unlocked module is unlocked
      const lessons = lessonSeqs.map((seq) => {
        const type = inferLessonType(seq);
        // Real duration if the course structure provides one.
        // edX exposes this under different keys depending on backend
        // (durationSeconds, effort_time, etc.) — check the likely ones.
        const realDuration = formatDuration(
          seq.durationSeconds || seq.duration_seconds || seq.effortTime,
        );
          // Meta line: duration for video/reading (if known), type label otherwise.
        let meta = null;
        if (realDuration) {
          meta = realDuration;
        } else if (type === 'quiz') {
          meta = 'Quiz';
        } else if (type === 'reading' || type === 'ebook') {
          meta = 'Lettura';
        } else if (type === 'exam') {
          meta = 'Esame';
        }
        // Three-state completion + locked.
        let status = 'not_started';
        if (seq.complete === true) {
          status = 'completed';
        } else if (
          typeof seq.completion === 'number' && seq.completion > 0
        ) {
          status = 'in_progress';
        } else if (seq.bookmarked === true) {
          status = 'in_progress';
        }
        // La lezione che si sta guardando non è mai bloccata: ci sei sopra.
        // Senza questa eccezione restava col lucchetto — e con `href` nullo,
        // quindi nemmeno cliccabile — finché la precedente non risultava
        // completata, che è proprio la condizione in cui ci si trova mentre la
        // si sta seguendo.
        // Le lezioni senza contenuto completabile non si contano nel
        // progresso, ne' a numeratore ne' a denominatore: e' la regola gia'
        // decisa dal cliente e applicata lato edX da `lezioni_con_contenuto`
        // (progress_push.py in igea-edx), dove tenerle nel solo denominatore
        // renderebbe il 100% irraggiungibile. Il dato che arriva fin qui e'
        // `completion_stat.completable_children` della sequenza, cioe' quanti
        // dei suoi figli sono completabili: zero vuol dire che dentro non c'e'
        // niente da fare (una copertina, una pagina di soli link).
        //
        // Restano elencate e cliccabili: quello che cambia e' solo se entrano
        // nel conto. E quando il dato manca — l'API puo' non mandare
        // `completion_stat` — la lezione si conta: mai scartare per ignoranza,
        // altrimenti il totale scenderebbe in silenzio.
        const completabile = typeof seq.completionStat?.total === 'number'
          ? seq.completionStat.total > 0
          : true;
        const corrente = seq.id === sequenceId;
        const locked = propedeuticita && !corrente && (moduleLocked || !prevLessonCompleted);
        // After processing this lesson, update the lookahead flag for
        // the NEXT iteration. We treat anything not-fully-completed
        // as "previous not done" so the user must finish each step.
        prevLessonCompleted = status === 'completed';
        return {
          id: seq.id,
          title: seq.title,
          type,
          duration: realDuration,
          href: locked ? null : `/course/${courseId}/${seq.id}`,
          meta,
          status,
          locked,
          completabile,
        };
      });
      // A module is complete only when every lesson is completed.
      const allLessonsComplete = lessons.length > 0
        && lessons.every((l) => l.status === 'completed');
      // After processing this section, set the gate for the next one.
      prevModuleCompleted = allLessonsComplete;
      return {
        id: section.id,
        title: section.title,
        lessons,
        locked: moduleLocked,
      };
    });
  }, [outline, courseId, sequenceId, propedeuticita]);

  // Current sequence title for the header.
  const currentSequenceTitle = useMemo(() => {
    if (!sequenceId || !outline?.sequences) { return ''; }
    return outline.sequences[sequenceId]?.title || '';
  }, [outline, sequenceId]);

  // Progress counts.
  // Si contano le stesse lezioni che contano gli anelli dei moduli, e si
  // contano da `modules` — cioe' dall'unico posto dove la regola e' scritta.
  // Prima si ripartiva da `outline.sequences`: due percorsi diversi verso lo
  // stesso numero, e appena uno dei due impara qualcosa (qui: scartare le
  // lezioni senza contenuto completabile) la stessa schermata mostra due
  // percentuali diverse, che e' peggio di non mostrarne nessuna.
  const { completedLessons, totalLessons } = useMemo(() => {
    const contabili = modules
      .flatMap((m) => m.lessons)
      .filter((l) => l.completabile);
    return {
      totalLessons: contabili.length,
      completedLessons: contabili.filter((l) => l.status === 'completed').length,
    };
  }, [modules]);

  return (
    <div
      className="tw-h-screen tw-bg-igea-bg tw-font-sans tw-text-text tw-flex tw-flex-col tw-overflow-hidden"
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <IgeaHeader title={currentSequenceTitle} />

      <div className="tw-flex tw-flex-col md:tw-flex-row tw-w-full tw-max-w-[1600px] tw-mx-auto tw-flex-1 tw-min-h-0 tw-p-4 tw-gap-4">
        <IgeaSidebar
          courseTitle={course?.title || 'Contenuto del corso'}
          courseDescription={course?.shortDescription || ''}
          totalLessons={totalLessons}
          completedLessons={completedLessons}
          modules={modules}
          currentUnitId={sequenceId || unitId}
          propedeuticita={propedeuticita}
        />
        <IgeaMainContent
          courseId={courseId}
          sequenceId={sequenceId}
          unitId={unitId}
          propedeuticita={propedeuticita}
          sequenceIds={orderedSeqIds}
        />
      </div>
    </div>
  );
};

IgeaPlayerLayout.propTypes = {
  courseId: PropTypes.string.isRequired,
  sequenceId: PropTypes.string,
  unitId: PropTypes.string,
};

IgeaPlayerLayout.defaultProps = {
  sequenceId: null,
  unitId: null,
};

export default IgeaPlayerLayout;
